/**
 * Client typé pour les routes `/homes` — Phase 4, incrément 4.4.
 *
 * Reflète la surface de `apps/api/src/routes/homes.ts`. Le client
 * frontend l'utilise quand l'utilisateur est authentifié (cf.
 * `stores/session.ts`). En mode anonymous, le store local
 * `stores/homes.ts` reste seul source de vérité — c'est l'orchestration
 * (Phase 4.5+) qui décidera de la stratégie de merge.
 *
 * Phase 4.4 : client typé uniquement, **pas de wiring automatique
 * dans `stores/homes.ts`**. Le code peut être appelé depuis l'UI
 * (Settings, futur écran de gestion) sans casser le store local.
 */
import { apiRequest } from './client.js';

export type HomeMode = 'classic' | 'coparenting';
export type MembershipRole = 'parent' | 'teen' | 'child';

/**
 * Home tel que retourné par l'API. `myRole` est enrichi côté API
 * pour permettre au client de griser les actions hors-permission
 * sans une requête supplémentaire.
 */
export interface ApiHome {
  id: string;
  name: string;
  mode: HomeMode;
  color: string | null;
  createdAt: string; // ISO 8601 (timestamptz)
  updatedAt: string;
  myRole: MembershipRole;
}

/** Membre d'un foyer tel qu'exposé par GET /homes/:id/members. */
export interface ApiMember {
  userId: string;
  email: string;
  displayName: string;
  lang: string;
  role: MembershipRole;
  joinedAt: string;
}

export interface CreateHomeInput {
  name: string;
  mode: HomeMode;
  color?: string;
}

export interface UpdateHomeInput {
  name?: string;
  /** `null` efface explicitement la couleur custom. */
  color?: string | null;
}

/**
 * Liste les foyers du user courant (via memberships).
 * Throw `ApiError` 401 si pas authentifié.
 */
export async function listHomes(): Promise<ApiHome[]> {
  const data = await apiRequest<{ homes: ApiHome[] }>('/homes');
  return data.homes;
}

/**
 * Détail d'un home. Throw `ApiError` 403 si pas membre.
 */
export async function getHome(id: string): Promise<ApiHome> {
  const data = await apiRequest<{ home: ApiHome }>(`/homes/${id}`);
  return data.home;
}

/**
 * Crée un foyer + auto-membership du user comme `parent`.
 */
export async function createHome(input: CreateHomeInput): Promise<ApiHome> {
  const data = await apiRequest<{ home: ApiHome }>('/homes', {
    method: 'POST',
    body: input,
  });
  return data.home;
}

/**
 * Met à jour name / color. Réservé aux `parent` du foyer.
 * Throw `ApiError` 403 forbidden_role si l'utilisateur a un rôle
 * insuffisant.
 */
export async function updateHome(id: string, input: UpdateHomeInput): Promise<ApiHome> {
  const data = await apiRequest<{ home: ApiHome }>(`/homes/${id}`, {
    method: 'PATCH',
    body: input,
  });
  return data.home;
}

/**
 * Supprime un foyer + cascade memberships. Réservé aux `parent`.
 * Retourne `void` sur succès (204 No Content).
 */
export async function deleteHome(id: string): Promise<void> {
  await apiRequest<void>(`/homes/${id}`, { method: 'DELETE' });
}

/**
 * Liste les membres d'un foyer. Réservé aux `parent`. Retourne
 * tableau vide pour les autres rôles (mais l'API renvoie 403 —
 * le caller doit gérer l'`ApiError`).
 */
export async function listMembers(homeId: string): Promise<ApiMember[]> {
  const data = await apiRequest<{ members: ApiMember[] }>(`/homes/${homeId}/members`);
  return data.members;
}
